<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import type { BookingContactField } from '@/types/carts-transactions-api';

import { isValidEmail, isValidPhone } from '@/composables/carts/useContactFieldsForm';

const props = defineProps<{
  field: BookingContactField;
  inputId: string;
  modelValue: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const { t } = useI18n();

function handleMultiselectChange(optionValue: string, isChecked: boolean) {
  const current = props.modelValue.split(',').filter(value => value !== '');
  if (isChecked) {
    current.push(optionValue);
  }
  else {
    const index = current.indexOf(optionValue);
    if (index > -1)
      current.splice(index, 1);
  }
  emit('update:modelValue', current.join(','));
}

function isInvalid(type: string): boolean {
  const trimmed = props.modelValue.trim();
  if (!trimmed)
    return false;
  if (type === 'EMAIL')
    return !isValidEmail(trimmed);
  if (type === 'PHONE')
    return !isValidPhone(trimmed);
  return false;
}
</script>

<template>
  <!-- SELECT -->
  <div v-if="field.type === 'SELECT'" class="select-wrapper">
    <select
      :id="inputId"
      class="field-input field-select"
      :required="field.required"
      :aria-required="field.required ? 'true' : undefined"
      :value="modelValue"
      @change="emit('update:modelValue', ($event.target as HTMLSelectElement).value)"
    >
      <option value="" disabled>
        {{ t('cart.contactFields.selectPlaceholder') }}
      </option>
      <option
        v-for="option in field.availableValues"
        :key="option.value"
        :value="option.value"
      >
        {{ option.label }}
      </option>
    </select>
  </div>

  <!-- MULTISELECT -->
  <fieldset v-else-if="field.type === 'MULTISELECT'" class="multiselect-fieldset">
    <legend class="sr-only">
      {{ field.name }}
    </legend>
    <div class="multiselect-group">
      <label
        v-for="option in field.availableValues"
        :key="option.value"
        class="multiselect-option"
      >
        <input
          type="checkbox"
          :value="option.value"
          :checked="modelValue.split(',').includes(option.value)"
          @change="handleMultiselectChange(option.value, ($event.target as HTMLInputElement).checked)"
        >
        {{ option.label }}
      </label>
    </div>
  </fieldset>

  <!-- EMAIL / PHONE -->
  <div v-else-if="field.type === 'EMAIL' || field.type === 'PHONE'" class="validated-field">
    <input
      :id="inputId"
      :type="field.type === 'EMAIL' ? 'email' : 'tel'"
      :inputmode="field.type === 'EMAIL' ? 'email' : 'tel'"
      class="field-input"
      :class="{ 'field-input--invalid': isInvalid(field.type) }"
      :required="field.required"
      :aria-required="field.required ? 'true' : undefined"
      :aria-invalid="isInvalid(field.type) ? 'true' : undefined"
      :placeholder="field.name"
      :value="modelValue"
      @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    >
    <p v-if="isInvalid(field.type)" class="field-error">
      {{ field.type === 'EMAIL' ? t('cart.contactFields.invalidEmail') : t('cart.contactFields.invalidPhone') }}
    </p>
  </div>

  <!-- DATE (native browser date picker, value in yyyy-MM-dd) -->
  <input
    v-else-if="field.type === 'DATE'"
    :id="inputId"
    type="date"
    class="field-input field-date"
    :class="{ 'field-date--empty': !modelValue }"
    :required="field.required"
    :aria-required="field.required ? 'true' : undefined"
    :value="modelValue"
    @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
  >

  <!-- TEXT (default) -->
  <input
    v-else
    :id="inputId"
    type="text"
    class="field-input"
    :required="field.required"
    :aria-required="field.required ? 'true' : undefined"
    :placeholder="field.name"
    :value="modelValue"
    @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
  >
</template>

<style scoped lang="css">
.select-wrapper {
  position: relative;
  width: 100%;
}

.select-wrapper::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 5l3 3 3-3' stroke='%23252525' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  pointer-events: none;
}

.field-input {
  width: 100%;
  padding: var(--yb-spacing-sm);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: var(--yb-radius-sm);
  color: var(--yb-color-black);
  background-color: var(--yb-color-white);
  box-sizing: border-box;
  /* >= 16px prevents iOS Safari from zooming on focus */
  font-size: var(--yb-text-base);

  &:focus {
    outline: none;
    border-color: var(--yb-color-primary);
  }

  &::placeholder {
    color: var(--yb-color-darkgray);
  }
}

.field-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding-right: 32px;
  cursor: pointer;
}

.field-date {
  /* Keeps the native picker light on dark-mode OS, matching the other inputs */
  color-scheme: light;
  /* iOS Safari collapses an empty date input */
  min-height: calc(var(--yb-text-base) * 1.5 + 2 * var(--yb-spacing-sm));
  font-family: inherit;
  cursor: pointer;
}

/* The empty date mask (dd/mm/yyyy) acts as the placeholder */
.field-date--empty {
  color: var(--yb-color-darkgray);
}

.multiselect-fieldset {
  border: none;
  padding: 0;
  margin: 0;
}

.multiselect-group {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-xs);
}

.multiselect-option {
  display: flex;
  align-items: center;
  gap: var(--yb-spacing-xs);
  font-size: var(--yb-text-sm);
  cursor: pointer;

  input[type='checkbox'] {
    accent-color: var(--yb-color-primary);
    color-scheme: light;
  }
}

.validated-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-input--invalid {
  border-color: #ea3532;
}

.field-error {
  font-size: var(--yb-text-xs);
  color: #ea3532;
  margin: 0;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
